import { CreditCard, TriangleAlert, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  hasCardExpired,
  isCardExpiringBeforeRenewal,
  isSubscriptionPastDue,
} from '@/shared/domain'
import { formatLongDate } from '@/shared/infra/format'
import { useDismissibleBanner, useOrgContext } from '@/shared/ui/hooks'
import { Button } from '@/shared/ui/primitives'

import { cardLabel, formatCardExpiry } from './card-copy'

export function BillingBanners() {
  const {
    orgId,
    memberships,
    subscriptionStatus,
    subscriptionCancelAt,
    subscriptionCurrentPeriodEnd,
    defaultCardBrand,
    defaultCardLast4,
    defaultCardExpMonth,
    defaultCardExpYear,
  } = useOrgContext()

  if (!orgId) return null

  const orgName = memberships.find((m) => m.orgId === orgId)?.orgName ?? 'your organization'

  if (isSubscriptionPastDue(subscriptionStatus)) {
    return <PaymentFailedBanner orgName={orgName} cancelAt={subscriptionCancelAt} />
  }

  if (
    isCardExpiringBeforeRenewal({
      expMonth: defaultCardExpMonth,
      expYear: defaultCardExpYear,
      currentPeriodEnd: subscriptionCurrentPeriodEnd,
    })
  ) {
    return (
      <CardExpiryBanner
        orgId={orgId}
        orgName={orgName}
        brand={defaultCardBrand}
        last4={defaultCardLast4}
        expMonth={defaultCardExpMonth as number}
        expYear={defaultCardExpYear as number}
        currentPeriodEnd={subscriptionCurrentPeriodEnd}
      />
    )
  }

  return null
}

function UpdatePaymentMethodButton({ className }: { className: string }) {
  const navigate = useNavigate()
  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      onClick={() => navigate('/settings?tab=membership')}
      className={className}
    >
      Update payment method
    </Button>
  )
}

function PaymentFailedBanner({
  orgName,
  cancelAt,
}: {
  orgName: string
  cancelAt: string | undefined
}) {
  const deadline = cancelAt ? formatLongDate(cancelAt) : ''
  const action = deadline
    ? `Update your payment method by ${deadline} or your subscription will be canceled.`
    : 'Update your payment method to keep your subscription active.'

  return (
    <div
      data-testid="billing-payment-failed-banner"
      role="alert"
      className="flex shrink-0 items-center justify-between gap-2 border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-2xs font-semibold text-destructive sm:text-xs"
    >
      <div className="flex flex-1 items-center justify-center gap-2 text-center">
        <TriangleAlert className="size-4 shrink-0" aria-hidden />
        <span>
          Payment failed — we couldn&apos;t charge the card on file for {orgName}. {action}
        </span>
      </div>
      <UpdatePaymentMethodButton className="shrink-0 border-destructive/50 bg-transparent text-destructive hover:bg-destructive/10" />
    </div>
  )
}

function CardExpiryBanner({
  orgId,
  orgName,
  brand,
  last4,
  expMonth,
  expYear,
  currentPeriodEnd,
}: {
  orgId: string
  orgName: string
  brand: string | undefined
  last4: string | undefined
  expMonth: number
  expYear: number
  currentPeriodEnd: string | undefined
}) {
  const { dismissed, dismiss } = useDismissibleBanner(
    `cc-card-expiry-banner:${orgId}:${expMonth}-${expYear}:${currentPeriodEnd ?? 'unknown'}`,
  )
  if (dismissed) return null

  const card = cardLabel(brand, last4)
  const expired = hasCardExpired({ expMonth, expYear })
  const renewal = currentPeriodEnd ? formatLongDate(currentPeriodEnd) : ''
  const message = expired
    ? `The ${card} for ${orgName} has expired. Update it to avoid an interruption.`
    : renewal
      ? `The ${card} for ${orgName} expires ${formatCardExpiry(expMonth, expYear)} — before your renewal on ${renewal}. Update it to avoid an interruption.`
      : `The ${card} for ${orgName} expires ${formatCardExpiry(expMonth, expYear)} — before your next renewal. Update it to avoid an interruption.`

  return (
    <div
      data-testid="billing-card-expiry-banner"
      role="status"
      className="flex shrink-0 items-center justify-between gap-2 border-b border-amber/40 bg-amber-soft px-4 py-2 text-2xs font-semibold text-amber sm:text-xs"
    >
      <div className="flex flex-1 items-center justify-center gap-2 text-center">
        <CreditCard className="size-4 shrink-0" aria-hidden />
        <span>{message}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <UpdatePaymentMethodButton className="border-amber/50 bg-transparent text-amber hover:bg-amber/10" />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={dismiss}
          aria-label="Dismiss card expiry warning"
          className="text-amber hover:bg-amber/10"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
    </div>
  )
}
